import React, { useEffect, useState } from 'react';
import { AppPage } from './app-page';
import { useRouterContext, useServices } from '../../contexts';
import { Box, Text, TextButton, Heading } from '@wix/design-system';
import { RoutePath } from '../../models';
import WixLogo from '../../assets/images/wix-logo';
import EmptyDesert from '../../assets/images/empty-desert';
import { ChevronRight } from '@wix/wix-ui-icons-common';
import type { VersionType } from '@wix/app-market-services';
import { useTranslation } from '../../contexts/translations-context';

export function UnlistedAppPage({ shareUrlId }: { shareUrlId: string }) {
  const services = useServices();
  const [appId, setAppId] = useState<string | undefined>(undefined);
  const [versionType, setVersionType] = useState<VersionType | undefined>(
    undefined,
  );
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { router } = useRouterContext();
  const isTestMode = versionType === 'DEV_VERSION';

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const dcShareUrl = await services.devCenterShareUrl.get({ shareUrlId });
        if (dcShareUrl.activisionStatus === 'DISABLED') {
          return;
        }
        setIsActive(true);
        setVersionType(dcShareUrl.versionType);
        const app = await services.marketApp.get({ appId: dcShareUrl.appId });
        if (
          app.status === 'PUBLISHED' &&
          dcShareUrl.versionType !== 'DEV_VERSION'
        ) {
          router.navigateTo(
            {
              path: RoutePath.APP_PAGE,
              payload: {
                slug: dcShareUrl.appId,
                referral: `unlisted-app-${dcShareUrl.appId}`,
              },
            },
            { replace: true },
          );
        } else {
          setAppId(dcShareUrl.appId);
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
      setIsLoading(false);
    })();
  }, []);

  if (isLoading) {
    return null;
  }
  if (!appId || !isActive) {
    return <NoneActivePage mode={isTestMode ? 'test' : 'unlisted'} />;
  }
  return (
    <AppPage
      id={appId}
      status={isTestMode ? 'DRAFT' : 'APPROVED'}
      options={{
        pricingOptions: undefined,
        showRating: false,
        showReviews: false,
        showRecommendedForYou: false,
        mode: isTestMode ? 'test' : 'unlisted',
      }}
    />
  );
}

const NoneActivePage = ({ mode }: { mode: 'test' | 'unlisted' }) => {
  const { t } = useTranslation();

  return (
    <Box position="relative" height="100vh" width="100vw">
      <Box padding={6} width={80} position="absolute">
        <WixLogo />
      </Box>
      <Box
        position="absolute"
        direction="vertical"
        width="340px"
        align="center"
        top="50vh"
        left="50vw"
        transform="translate(-50%, -50%)"
        gap={2}
      >
        <Box width={120}>
          <EmptyDesert />
        </Box>
        <Box textAlign="center">
          <Heading size="extraLarge" as="h1">
            {t(`unlisted.page.emptyState.${mode}.header`)}
          </Heading>
        </Box>
        <Text>{t('unlisted.page.emptyState.text')}</Text>
        <TextButton as="a" href="https://wix.com" suffixIcon={<ChevronRight />}>
          {t('unlisted.page.emptyState.textButton')}
        </TextButton>
      </Box>
    </Box>
  );
};
